import { useEffect, useState } from 'react';
import Select from 'react-select';
import { Button } from '../../common/Button';
import { Input } from '../../common/Input';

import styles from './Form.module.css';
import { FormField, FormOption, FormProps } from './types';

export function Form({
  fields,
  initialValues,
  submitLabel = 'Guardar',
  onSubmit,
  onFieldChange,
}: FormProps) {
  const [values, setValues] =
    useState<Record<string, unknown>>(
      initialValues ?? {}
    );
  const [errors, setErrors] =
    useState<Record<string, string>>({});

  useEffect(() => {
    setValues(initialValues ?? {});
  }, [initialValues]);

  const handleChange = (
    name: string,
    value: unknown
  ) => {
    const newValues = {
      ...values,
      [name]: value,
    };

    setValues(newValues);

    setErrors((current) => ({
      ...current,
      [name]: '',
    }));

    onFieldChange?.(
      name,
      value,
      newValues
    );
  };

  const handleSubmit = (
    e: React.FormEvent
  ) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};

    fields.forEach((field) => {
      // Los campos opcionales no se validan
      if (field.optional) {
        return;
      }

      const value = values[field.name];

      const isEmpty =
        value === undefined ||
        value === null ||
        (Array.isArray(value) && value.length === 0) ||
        String(value).trim() === '';

      if (isEmpty) {
        newErrors[field.name] =
          `El campo "${field.label}" es obligatorio`;
      }
      const validationError =
        field.validation?.(value, values);

      if (validationError) {
        newErrors[field.name] = validationError;
      }
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    const submittedValues = { ...values };
    fields.forEach((field) => {
      if (field.type === 'select' && field.multiple) {
        submittedValues[field.name] = values[field.name] ?? [];
      }
    });
    onSubmit(submittedValues);
  };

  const renderField = (
    field: FormField
  ) => {
    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            className={styles.textarea}
            value={String(
              values[field.name] ?? ''
            )}
            onChange={(e) =>
              handleChange(
                field.name,
                e.target.value
              )
            }
            placeholder={
              field.placeholder
            }
          />
        );

      case 'select': {
        const selectedValue = values[field.name];
        const options = field.optionGroups
          ? field.optionGroups.flatMap((group) => group.options)
          : field.options ?? [];
        if (field.multiple) {
          const selected = Array.isArray(selectedValue) ? selectedValue : [];
          return (
            <Select<FormOption, true>
              isMulti
              isClearable
              closeMenuOnSelect={false}
              aria-label={field.label}
              aria-invalid={Boolean(errors[field.name])}
              options={field.optionGroups ?? options}
              value={options.filter((option) => selected.some((value) => String(value) === String(option.value)))}
              getOptionValue={(option) => String(option.value)}
              onChange={(selection) => handleChange(field.name, selection.map((option) => option.value))}
              placeholder={field.placeholder ?? 'Selecciona opciones...'}
              noOptionsMessage={() => 'No hay opciones disponibles'}
              styles={{
                control: (base) => ({ ...base, minHeight: 46, borderRadius: 8, borderColor: errors[field.name] ? '#d32f2f' : '#d0d7de', fontSize: 14 }),
                multiValue: (base) => ({ ...base, backgroundColor: '#eef2f6', borderRadius: 4 }),
                menu: (base) => ({ ...base, zIndex: 10 }),
              }}
            />
          );
        }
        return (
          <select
            className={styles.select}
            aria-label={field.label}
            aria-invalid={Boolean(errors[field.name])}
            value={String(selectedValue ?? '')}
            onChange={(e) =>
              handleChange(field.name, e.target.value)
            }
          >
            <option value="">Seleccionar...</option>

            {field.optionGroups
              ? field.optionGroups.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </optgroup>
              ))
              : field.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
          </select>
        );
      }

      case 'date':
        return (
          <Input
            label=""
            type="date"
            value={String(values[field.name] ?? '')}
            onChange={(value) =>
              handleChange(field.name, value)
            }
          />
        );

      default:
        return (
          <Input
            label=""
            type={field.type}
            value={String(
              values[field.name] ?? ''
            )}
            placeholder={
              field.placeholder
            }
            onChange={(value) =>
              handleChange(
                field.name,
                typeof value ===
                  'string'
                  ? value
                  : (
                    value as React.ChangeEvent<HTMLInputElement>
                  ).target.value
              )
            }
          />
        );
    }
  };

  return (
    <form
      className={styles.form}
      onSubmit={handleSubmit}
    >
      {fields.map((field) => (
        <div
          key={field.name}
          className={styles.field}
        >
          <label className={styles.label}>
            {field.label}

            {!field.optional && (
              <span className={styles.required}>
                *
              </span>
            )}
          </label>

          {renderField(field)}

          {errors[field.name] && (
            <span className={styles.error}>
              {errors[field.name]}
            </span>
          )}
        </div>
      ))}

      <div className={styles.actions}>
        <Button type="submit">
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
